/**
 * Library hearts — progressive enhancement that injects a save/unsave
 * heart button into every station surface on the page:
 *
 *   - any `a[data-station]` card (home, genre, country, top-50, related…)
 *   - the station-page hero (`[data-station-play]`)
 *
 * Cards are SSG/edge-cached, so per-user state can never be rendered
 * server-side; hearts hydrate from localStorage after paint instead.
 *
 * Loaded once per session from Layout.astro (bundled module). Astro view
 * transitions swap the DOM without re-running module scripts, so we
 * re-scan on `astro:page-load`.
 *
 * Also owns the one-shot migration of the previous site's localStorage
 * favourites (`favoritesData`) into the new Library — see
 * runLegacyMigration() below.
 */
import type { PlayerStation } from './player-bus';
import {
  appendMigratedStations,
  isSaved,
  legacyMigrationInfo,
  markLegacyMigrated,
  mergeBridgedLegacyFavorites,
  onLibraryChange,
  readLegacyFavorites,
  readLibrary,
  toggleStation,
  type LegacyFavorite,
  type LibraryStation,
} from './library-store';

const HEART_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
  '<path d="M12 21s-6.7-4.35-9.33-8.11C.9 10.36 1.42 6.95 3.9 5.34a5.06 5.06 0 0 1 6.42.9L12 7.9l1.68-1.66a5.06 5.06 0 0 1 6.42-.9c2.48 1.61 3 5.02 1.23 7.55C18.7 16.65 12 21 12 21Z"/>' +
  '</svg>';

function parsePayload(raw: string | null): PlayerStation | null {
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as PlayerStation;
    return p && p.uuid && p.directUrl && p.proxyUrl ? p : null;
  } catch {
    return null;
  }
}

function setPressed(btn: HTMLButtonElement, saved: boolean, name: string): void {
  btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
  btn.setAttribute(
    'aria-label',
    saved ? `Remove ${name} from Library` : `Save ${name} to Library`,
  );
  btn.title = saved ? 'In your Library' : 'Save to Library';
}

function makeHeart(payload: PlayerStation, extraClass = ''): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = `ot-heart${extraClass ? ` ${extraClass}` : ''}`;
  btn.dataset['heartUuid'] = payload.uuid;
  btn.innerHTML = HEART_SVG;
  setPressed(btn, isSaved(payload.uuid), payload.name);
  btn.addEventListener('click', (e) => {
    // Hearts live inside <a> cards — never navigate, never trigger the
    // Layout's global click-to-play handler.
    e.preventDefault();
    e.stopPropagation();
    const saved = toggleStation(payload);
    setPressed(btn, saved, payload.name);
    // Micro pop animation hook.
    btn.dataset['pop'] = '1';
    window.setTimeout(() => delete btn.dataset['pop'], 350);
  });
  return btn;
}

function scan(): void {
  // Cards.
  const cards = document.querySelectorAll<HTMLElement>(
    'a[data-station]:not([data-heart-wired]):not([data-no-heart])',
  );
  for (const card of cards) {
    card.dataset['heartWired'] = '1';
    const payload = parsePayload(card.getAttribute('data-station'));
    if (!payload) continue;

    // iOS-style rows pre-render their heart button ([data-heart-slot],
    // .ot-heart2) — wire it instead of injecting the legacy absolute
    // heart.
    const slot = card.querySelector<HTMLButtonElement>('[data-heart-slot]');
    if (slot) {
      setPressed(slot, isSaved(payload.uuid), payload.name);
      slot.dataset['heartUuid'] = payload.uuid;
      slot.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const saved = toggleStation(payload);
        setPressed(slot, saved, payload.name);
      });
      continue;
    }

    if (getComputedStyle(card).position === 'static') {
      card.style.position = 'relative';
    }
    card.appendChild(makeHeart(payload, heartVariant(card)));
  }

  // Station-page hero.
  const hero = document.querySelector<HTMLElement>(
    '[data-station-play]:not([data-heart-wired])',
  );
  if (hero) {
    hero.dataset['heartWired'] = '1';
    const payload = parsePayload(hero.dataset['stationPayload'] ?? null);
    if (payload) hero.appendChild(makeHeart(payload, 'ot-heart--hero'));
  }
}

/** Per-surface placement modifier — keeps the heart clear of card content. */
function heartVariant(card: HTMLElement): string {
  if (card.classList.contains('row')) return 'ot-heart--row';
  if (card.classList.contains('mrow')) return 'ot-heart--mrow';
  if (
    card.classList.contains('card') ||
    card.classList.contains('featured-card') ||
    card.classList.contains('related-card')
  ) {
    return 'ot-heart--bottom';
  }
  return '';
}

/** Keep every heart on the page in sync with the store. */
function syncHearts(): void {
  const hearts = document.querySelectorAll<HTMLButtonElement>(
    '.ot-heart[data-heart-uuid], .ot-heart2[data-heart-uuid]',
  );
  for (const btn of hearts) {
    const uuid = btn.dataset['heartUuid']!;
    const pressed = btn.getAttribute('aria-pressed') === 'true';
    const saved = isSaved(uuid);
    if (pressed !== saved) {
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    }
  }
}

// ── Legacy favourites migration ─────────────────────────────────────────

interface ResolvedPayload extends PlayerStation {
  live: boolean;
}

async function resolveByUuid(uuids: string[]): Promise<Map<string, ResolvedPayload>> {
  const out = new Map<string, ResolvedPayload>();
  for (let i = 0; i < uuids.length; i += 100) {
    const batch = uuids.slice(i, i + 100);
    try {
      const res = await fetch('/api/library-resolve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uuids: batch }),
      });
      if (!res.ok) continue;
      const body = (await res.json()) as { stations?: ResolvedPayload[] };
      for (const s of body.stations ?? []) out.set(s.uuid, s);
    } catch {
      /* network hiccup — unresolved uuids fall back below */
    }
  }
  return out;
}

async function signUrl(url: string): Promise<string | null> {
  try {
    const res = await fetch('/api/sign-stream', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { proxyUrl?: string };
    return body.proxyUrl ?? null;
  } catch {
    return null;
  }
}

/**
 * One-shot import of the previous site's `favoritesData`. Runs at idle
 * on any page; guarded by a persistent flag so it executes at most once
 * per browser. Resolution strategy:
 *   1. Batch uuid → canonical payload from our snapshot (slug + fresh
 *      stream URL + signed proxy path).
 *   2. Stations missing from the snapshot keep their legacy URL and get
 *      a proxy path signed on demand — still playable, just no /station
 *      page link.
 */
let migrationInFlight: Promise<void> | null = null;

export function runLegacyMigration(): Promise<void> {
  if (migrationInFlight) return migrationInFlight;
  migrationInFlight = performLegacyMigration().finally(() => {
    migrationInFlight = null;
  });
  return migrationInFlight;
}

async function performLegacyMigration(): Promise<void> {
  const legacy = readLegacyFavorites();
  const info = legacyMigrationInfo();
  if (legacy.length === 0) {
    if (!info) markLegacyMigrated(0);
    return;
  }

  // Retry a prior imported:0 probe, and repair partial storage loss when
  // the migration flag survived but one or more stations did not.
  if (info?.imported) {
    const saved = new Set(readLibrary().stations.map((station) => station.uuid));
    if (legacy.every((favorite) => saved.has(favorite.stationuuid))) return;
  }

  const resolved = await resolveByUuid(legacy.map((f) => f.stationuuid));
  const migrated: LibraryStation[] = [];

  for (const fav of legacy) {
    const hit = resolved.get(fav.stationuuid);
    if (hit) {
      const { live, ...payload } = hit;
      migrated.push({
        ...payload,
        slug: live ? payload.slug : '',
        custom: false,
        savedAt: Date.now(),
      });
      continue;
    }
    // Not in our snapshot — keep the legacy stream as a custom-like entry.
    const legacyEntry = await migrateUnresolved(fav);
    if (legacyEntry) migrated.push(legacyEntry);
  }

  // Flag BEFORE appending: appendMigratedStations dispatches the
  // library-changed event, and listeners (LibraryView banner) read the
  // flag synchronously from that handler.
  markLegacyMigrated(migrated.length);
  appendMigratedStations(migrated);
}

async function migrateUnresolved(fav: LegacyFavorite): Promise<LibraryStation | null> {
  const url = (fav.url_resolved || fav.url || '').trim();
  if (!/^https?:\/\//i.test(url)) return null;
  const proxyUrl = await signUrl(url);
  if (!proxyUrl) return null;
  return {
    uuid: fav.stationuuid,
    name: fav.name || 'Radio station',
    slug: '',
    directUrl: url,
    proxyUrl,
    color: '#30B8C8',
    country: fav.country ?? null,
    codec: fav.codec ?? null,
    bitrate: fav.bitrate ?? null,
    custom: false,
    savedAt: Date.now(),
  };
}

// ── www → apex legacy-storage bridge ───────────────────────────────────

function initWwwLegacyBridge(): void {
  if (location.protocol !== 'https:' || location.hostname !== 'opentune.net') return;

  const iframe = document.createElement('iframe');
  iframe.src = 'https://www.opentune.net/library-bridge';
  iframe.hidden = true;
  iframe.title = 'OpenTune legacy library migration';
  iframe.setAttribute('aria-hidden', 'true');

  let settled = false;
  const cleanup = () => {
    if (settled) return;
    settled = true;
    window.removeEventListener('message', onMessage);
    iframe.remove();
  };
  const onMessage = (event: MessageEvent) => {
    if (
      event.origin !== 'https://www.opentune.net' ||
      event.source !== iframe.contentWindow ||
      !event.data ||
      event.data.type !== 'ot-legacy-library'
    ) return;
    const raw = event.data.favoritesData;
    if (typeof raw === 'string' && raw.length > 0) {
      mergeBridgedLegacyFavorites(raw);
      void runLegacyMigration();
    }
    cleanup();
  };

  window.addEventListener('message', onMessage);
  document.body.appendChild(iframe);
  window.setTimeout(cleanup, 8_000);
}

// ── Init ────────────────────────────────────────────────────────────────

declare global {
  interface Window {
    __otHeartsInit?: boolean;
  }
}

export function initLibraryHearts(): void {
  if (typeof window === 'undefined' || window.__otHeartsInit) return;
  window.__otHeartsInit = true;

  scan();
  document.addEventListener('astro:page-load', scan);
  // Client-side list growth (genre “Load more”, future infinite
  // scrolls) announces new [data-station] nodes with this event.
  window.addEventListener('ot-hearts-rescan', scan);
  onLibraryChange(syncHearts);
  initWwwLegacyBridge();

  const idle: (cb: () => void) => void =
    'requestIdleCallback' in window
      ? (cb) => window.requestIdleCallback(() => cb(), { timeout: 5000 })
      : (cb) => window.setTimeout(cb, 1500);
  idle(() => void runLegacyMigration());
}
